import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SellerLayout } from '../components/SellerLayout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { getMySellerShows, cancelShow } from '../api/client';
import type { SellerShow } from '../api/client';
import { parseUTC } from '../utils/date';
import { Video, Calendar, Clock, ChevronRight, Plus, X } from 'lucide-react';

function formatDateTime(dateString: string): string {
  return parseUTC(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function formatDuration(startedAt: string | null, endedAt: string | null): string {
  if (!startedAt || !endedAt) return '--';
  const start = parseUTC(startedAt);
  const end = parseUTC(endedAt);
  const diffMs = end.getTime() - start.getTime();
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}


function formatScheduledTime(scheduledAt: string): string {
  const date = parseUTC(scheduledAt);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `In ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  } else if (diffHours > 0) {
    return `In ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  } else {
    const diffMins = Math.floor(diffMs / (1000 * 60));
    return diffMins > 0 ? `In ${diffMins} min` : 'Start now';
  }
}

export function SellerShowsPage() {
  useDocumentTitle('Shows');
  const navigate = useNavigate();
  const [upcomingShows, setUpcomingShows] = useState<SellerShow[]>([]);
  const [pastShows, setPastShows] = useState<SellerShow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const handleCancel = async (showId: string, showName: string) => {
    if (!window.confirm(`Cancel "${showName}"? This cannot be undone.`)) return;
    try {
      setCancellingId(showId);
      await cancelShow(showId);
      setUpcomingShows((prev) => prev.filter((s) => s.id !== showId));
    } catch (err) {
      console.error('Failed to cancel show:', err);
      alert('Failed to cancel show. Please try again.');
    } finally {
      setCancellingId(null);
    }
  };

  useEffect(() => {
    const loadShows = async () => {
      try {
        setIsLoading(true);
        const allShows = await getMySellerShows();

        const scheduled = allShows
          .filter((s) => s.status === 'SCHEDULED')
          .sort((a, b) => parseUTC(a.scheduledAt).getTime() - parseUTC(b.scheduledAt).getTime());
        setUpcomingShows(scheduled);

        const ended = allShows
          .filter((s) => s.status === 'ENDED')
          .sort((a, b) => {
            const aTime = a.endedAt ? parseUTC(a.endedAt).getTime() : 0;
            const bTime = b.endedAt ? parseUTC(b.endedAt).getTime() : 0;
            return bTime - aTime;
          });
        setPastShows(ended);
      } catch (err) {
        console.error('Failed to load shows:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadShows();
  }, []);

  return (
    <SellerLayout>
      <div style={styles.container}>
        {/* Header Row */}
        <div style={styles.headerRow}>
          <h2 style={styles.sectionTitle}>Shows</h2>
          <div style={styles.headerActions}>
            <button
              style={styles.goLiveButton}
              onClick={() => navigate('/sell/live')}
            >
              <Video size={16} color="#FFFFFF" />
              <span>Go Live</span>
            </button>
            <button
              style={styles.scheduleButton}
              onClick={() => navigate('/sell/live?mode=schedule')}
            >
              <Plus size={16} color={colors.brand.blue} />
              <span>Schedule</span>
            </button>
          </div>
        </div>

        {isLoading ? (
          <div style={styles.loadingState}>Loading shows...</div>
        ) : (
          <>
            {/* Upcoming Shows */}
            {upcomingShows.length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.subsectionTitle}>Upcoming</h3>
                <div style={styles.showsList}>
                  {upcomingShows.map((show) => (
                    <div
                      key={show.id}
                      style={styles.showCard}
                      onClick={() => navigate(`/sell/live/${show.id}`)}
                    >
                      <div style={styles.showIconWrapper}>
                        <Calendar size={18} color={colors.brand.blue} />
                      </div>
                      <div style={styles.showContent}>
                        <span style={styles.showName}>{show.name}</span>
                        <div style={styles.showMeta}>
                          <Clock size={12} color={colors.gray[600]} />
                          <span style={styles.showTime}>
                            {formatScheduledTime(show.scheduledAt)}
                          </span>
                        </div>
                        {show.categories && show.categories.length > 0 && (
                          <div style={styles.showCategories}>
                            {show.categories.map((cat, i) => (
                              <span key={i} style={styles.categoryTag}>
                                {cat}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        style={styles.cancelButton}
                        onClick={(e) => { e.stopPropagation(); handleCancel(show.id, show.name); }}
                        disabled={cancellingId === show.id}
                        title="Cancel show"
                      >
                        <X size={14} color={colors.gray[600]} />
                      </button>
                      <button style={styles.startButton}>
                        Start
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Past Shows */}
            <div style={styles.section}>
              <h3 style={styles.subsectionTitle}>Past Shows</h3>
              {pastShows.length === 0 ? (
                <div style={styles.emptyState}>
                  <Video size={32} color={colors.gray[300]} />
                  <p style={styles.emptyText}>No past shows yet</p>
                </div>
              ) : (
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Show</th>
                        <th style={styles.th}>Date</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Items Sold</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Item Sales</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Duration</th>
                        <th style={{ ...styles.th, width: '32px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {pastShows.map((show) => {
                        return (
                          <tr
                            key={show.id}
                            style={styles.tr}
                            onClick={() => navigate(`/sell/shows/${show.id}`)}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLTableRowElement).style.backgroundColor = colors.gray[100];
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '';
                            }}
                          >
                            <td style={styles.td}>
                              <span style={styles.tableName}>{show.name}</span>
                            </td>
                            <td style={styles.td}>
                              <span style={styles.tableSecondary}>
                                {show.startedAt
                                  ? formatDateTime(show.startedAt)
                                  : show.scheduledAt
                                    ? formatDateTime(show.scheduledAt)
                                    : '--'}
                              </span>
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right' }}>
                              <span style={styles.tableSecondary}>
                                {show.saleCount}
                              </span>
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right' }}>
                              <span style={styles.tableSecondary}>
                                {formatCurrency(show.saleVolume)}
                              </span>
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right' }}>
                              <span style={styles.tableSecondary}>
                                {formatDuration(show.startedAt, show.endedAt)}
                              </span>
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right' }}>
                              <ChevronRight size={14} color={colors.gray[400]} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </SellerLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  goLiveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    backgroundColor: colors.accent.pink,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  scheduleButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    backgroundColor: colors.brand.blue + '15',
    color: colors.brand.blue,
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  section: {},
  subsectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.gray[600],
    margin: '0 0 12px 0',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  showsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  showCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
    cursor: 'pointer',
    transition: 'background-color 0.1s',
  },
  showIconWrapper: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    backgroundColor: colors.brand.blue + '15',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  showContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  showName: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  showMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '4px',
  },
  showTime: {
    fontSize: '13px',
    color: colors.gray[600],
  },
  showDate: {
    fontSize: '13px',
    color: colors.gray[600],
    marginTop: '4px',
  },
  showCategories: {
    display: 'flex',
    gap: '6px',
    marginTop: '8px',
  },
  categoryTag: {
    padding: '2px 8px',
    backgroundColor: colors.gray[100],
    borderRadius: '9999px',
    fontSize: '12px',
    color: colors.gray[600],
  },
  cancelButton: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  startButton: {
    padding: '8px 20px',
    backgroundColor: colors.accent.pink,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '9999px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
  },
  loadingState: {
    padding: '48px',
    textAlign: 'center',
    color: colors.gray[600],
    fontSize: '14px',
  },
  emptyState: {
    padding: '48px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  emptyText: {
    color: colors.gray[600],
    fontSize: '14px',
    margin: 0,
  },
  tableWrapper: {
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    padding: '10px 16px',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[600],
    textAlign: 'left' as const,
    borderBottom: `1px solid ${colors.gray[300]}`,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  tr: {
    cursor: 'pointer',
    transition: 'background-color 0.1s',
    borderBottom: `1px solid ${colors.gray[200]}`,
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    verticalAlign: 'middle' as const,
  },
  tableName: {
    fontWeight: 600,
    color: colors.ink.black,
  },
  tableSecondary: {
    color: colors.gray[600],
  },
};
